import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowRight, 
  FiMail, 
  FiCheckCircle, 
  FiUsers, 
  FiTarget, 
  FiEye, 
  FiCalendar, 
  FiExternalLink,
  FiSend
} = FiIcons;

function App() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [profile, setProfile] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const questions = [
    {
      id: 'q1',
      text: 'Nach einem klaren Gespräch mit deinem Team…',
      options: [
        { value: 'vermeider', text: '…sage ich nichts mehr – aus Respekt.' },
        { value: 'kontrolleur', text: '…frage ich täglich nach, ob alles läuft.' },
        { value: 'ermoeglicher', text: '…sende ich am nächsten Tag Wertschätzung + nächsten Schritt.' }
      ]
    },
    {
      id: 'q2',
      text: 'Wenn ein Projekt stockt, tendierst du dazu…',
      options: [
        { value: 'vermeider', text: '…abzuwarten, ob es sich von selbst löst.' },
        { value: 'kontrolleur', text: '…selbst einzuspringen und zu kontrollieren.' },
        { value: 'ermoeglicher', text: '…das Team zu fragen: „Was braucht ihr, um weiterzukommen?"' }
      ]
    },
    {
      id: 'q3',
      text: 'Dein Team beschreibt dein Follow-up vermutlich als…',
      options: [
        { value: 'vermeider', text: '„unsicher" oder „schweigsam"' },
        { value: 'kontrolleur', text: '„drängend" oder „misstrauisch"' },
        { value: 'ermoeglicher', text: '„klar" und „verlässlich"' }
      ]
    },
    {
      id: 'q4',
      text: 'Wie oft meldest du dich nach einem wichtigen Gespräch?',
      options: [
        { value: 'vermeider', text: 'Fast nie – ich will nicht stören.' },
        { value: 'kontrolleur', text: 'Mehrmals pro Woche – zur Sicherheit.' },
        { value: 'ermoeglicher', text: 'Einmal, mit klarem nächsten Schritt.' }
      ]
    },
    {
      id: 'q5',
      text: 'Was fürchtest du am meisten beim Follow-up?',
      options: [
        { value: 'vermeider', text: 'Dass ich als anstrengend wahrgenommen werde.' },
        { value: 'kontrolleur', text: 'Dass etwas schiefgeht, wenn ich nicht eingreife.' },
        { value: 'ermoeglicher', text: 'Dass Unklarheit bleibt.' }
      ]
    },
    {
      id: 'q6',
      text: 'Deine Standard-Nachricht nach einem Meeting lautet…',
      options: [
        { value: 'vermeider', text: '(Ich schreibe meist nichts.)' },
        { value: 'kontrolleur', text: '„Alles klar soweit? Braucht ihr was?"' },
        { value: 'ermoeglicher', text: '„Danke! Nächster Schritt: [konkrete Aktion] bis [Datum]."' }
      ]
    },
    {
      id: 'q7',
      text: 'Wenn jemand eine Zusage bricht, reagierst du mit…',
      options: [
        { value: 'vermeider', text: 'Schweigen – vielleicht hat er ja einen Grund.' },
        { value: 'kontrolleur', text: 'Sofortiger Nachfrage: „Warum hast du das nicht gemacht?"' },
        { value: 'ermoeglicher', text: 'Klarem Gespräch: „Was ist dazwischen gekommen? Wie stellen wir sicher, dass es klappt?"' }
      ]
    },
    {
      id: 'q8',
      text: 'Dein Team weiß nach einem Gespräch…',
      options: [
        { value: 'vermeider', text: '…nicht, ob es ernst gemeint war.' },
        { value: 'kontrolleur', text: '…dass du es genau beobachten wirst.' },
        { value: 'ermoeglicher', text: '…genau, was als Nächstes kommt.' }
      ]
    },
    {
      id: 'q9',
      text: 'Du glaubst, gutes Follow-up bedeutet…',
      options: [
        { value: 'vermeider', text: '…dem anderen Raum zu geben.' },
        { value: 'kontrolleur', text: '…sicherzustellen, dass nichts untergeht.' },
        { value: 'ermoeglicher', text: '…Klarheit zu schaffen – ohne Kontrolle.' }
      ]
    },
    {
      id: 'q10',
      text: 'Was würdest du dir im Follow-up wünschen?',
      options: [
        { value: 'vermeider', text: 'Mehr Mut, konsequent zu sein.' },
        { value: 'kontrolleur', text: 'Mehr Vertrauen ins Team.' },
        { value: 'ermoeglicher', text: 'Dass andere genauso klar kommunizieren wie ich.' }
      ]
    }
  ];

  const handleAnswerSelect = (value) => {
    setAnswers({ ...answers, [questions[currentQuestion].id]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResult();
    }
  };

  const calculateResult = () => {
    const counts = { vermeider: 0, kontrolleur: 0, ermoeglicher: 0 };
    Object.values(answers).forEach(answer => counts[answer]++);

    let resultProfile = "ermoeglicher";
    if (counts.vermeider > counts.kontrolleur && counts.vermeider >= 4) {
      resultProfile = "vermeider";
    } else if (counts.kontrolleur > counts.vermeider && counts.kontrolleur >= 4) {
      resultProfile = "kontrolleur";
    }

    setProfile(resultProfile);
    setShowResult(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getProfileContent = () => {
    switch (profile) {
      case 'vermeider':
        return {
          title: 'Du bist der Vermeider',
          shortName: 'Der Vermeider',
          icon: FiEye,
          color: 'from-amber-500 to-orange-600',
          textColor: 'text-amber-700',
          badgeColor: 'bg-amber-100 text-amber-700',
          description: 'Du führst klare Gespräche – aber danach ziehst du dich zurück. Dein Team fühlt sich unsicher: „War\'s das jetzt?"',
          blindspot: 'Du glaubst, Schweigen sei Respekt. Tatsächlich ist es Unsicherheit.',
          bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50'
        };
      case 'kontrolleur':
        return {
          title: 'Du bist der Kontrolleur',
          shortName: 'Der Kontrolleur',
          icon: FiTarget,
          color: 'from-red-500 to-pink-600',
          textColor: 'text-red-700',
          badgeColor: 'bg-red-100 text-red-700',
          description: 'Du willst Sicherheit schaffen – aber dein ständiges Nachfragen erzeugt Druck. Dein Team denkt: „Vertraut er mir nicht?"',
          blindspot: 'Du glaubst, Kontrolle sei Fürsorge. Tatsächlich ist es Misstrauen.',
          bgColor: 'bg-gradient-to-br from-red-50 to-pink-50'
        };
      default:
        return {
          title: 'Du bist der Ermöglicher',
          shortName: 'Der Ermöglicher',
          icon: FiCheckCircle,
          color: 'from-green-500 to-emerald-600',
          textColor: 'text-emerald-700',
          badgeColor: 'bg-green-100 text-emerald-700',
          description: 'Du gibst Klarheit + Freiraum. Dein Team fühlt sich sicher und verantwortlich.',
          blindspot: 'Du lebst bewusste Führung – herzlichen Glückwunsch!',
          bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50'
        };
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setEmailSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const currentAnswer = answers[questions[currentQuestion]?.id];

  if (emailSubmitted) {
    const profileContent = getProfileContent();
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[2.5rem] p-10 md:p-16 shadow-2xl border border-gray-100 text-center relative overflow-hidden"
          >
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -z-1" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-50 rounded-tr-full -z-1" />

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.2 }}
              className="w-24 h-24 mx-auto mb-8 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200"
            >
              <SafeIcon icon={FiCheckCircle} className="text-5xl text-white" />
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6"
            >
              Fast geschafft!
            </motion.h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className={`inline-block px-6 py-2 rounded-full font-bold text-lg mb-8 ${profileContent.badgeColor}`}
            >
              {profileContent.shortName}
            </motion.div>

            <div className="space-y-6 text-lg text-gray-600 mb-12 max-w-xl mx-auto leading-relaxed">
              <p>
                Dein maßgeschneidertes <strong className="text-gray-900">3-Tage-Nachfass-Template</strong> wird dir in den nächsten Minuten per E-Mail zugesendet.
              </p>
              <p className="text-sm italic flex items-center justify-center gap-2">
                <SafeIcon icon={FiSend} className="text-blue-500" />
                Bitte prüfe auch deinen Spam-Ordner.
              </p>
            </div>

            <hr className="border-gray-100 mb-12" />

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-slate-50 rounded-3xl p-8 border border-slate-100"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Willst du direkt mit deinem Blindspot arbeiten?
              </h3>
              <p className="text-gray-600 mb-8">
                Im Call zeige ich dir, wie du deinen Follow-up-Blindspot schließt – ohne mehr Zeit oder Energie zu investieren.
              </p>
              
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href="https://calendly.com/harald-suekar"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold py-5 px-10 rounded-2xl text-xl shadow-xl hover:shadow-2xl hover:shadow-blue-200 transition-all"
              >
                <SafeIcon icon={FiCalendar} />
                Kostenlos Strategy-Call buchen
                <SafeIcon icon={FiExternalLink} className="text-blue-200" />
              </motion.a>
              
              <p className="mt-6 text-sm text-gray-500">
                ➤ 15-Minuten Quick-Check für deine Führungskommunikation
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showResult) {
    const profileContent = getProfileContent();
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${profileContent.bgColor} rounded-3xl p-8 shadow-2xl border border-white/20`}
          >
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className={`w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r ${profileContent.color} flex items-center justify-center shadow-lg`}
              >
                <SafeIcon icon={profileContent.icon} className="text-3xl text-white" />
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-4xl font-bold text-gray-800 mb-4"
              >
                {profileContent.title}
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-lg text-gray-700 mb-6 leading-relaxed"
              >
                {profileContent.description}
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="bg-white/70 rounded-2xl p-6 border border-gray-200/50"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center justify-center gap-2">
                  <SafeIcon icon={FiEye} className="text-xl" />
                  Dein Blindspot:
                </h3>
                <p className="text-gray-700 font-medium">
                  {profileContent.blindspot}
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200/50"
            >
              <div className="text-center mb-6">
                <SafeIcon icon={FiMail} className="text-3xl text-blue-600 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Erhalte dein maßgeschneidertes
                </h3>
                <p className="text-xl text-blue-600 font-semibold">
                  3-Tage-Nachfass-Template
                </p>
              </div>
              
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <input
                  type="email"
                  placeholder="Deine E-Mail-Adresse"
                  required
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiMail} />
                  PDF kostenlos zusenden
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <SafeIcon icon={FiUsers} className="text-4xl text-blue-600" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
              Entdecke deinen Follow-up-Blindspot
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Finde heraus, welcher Führungstyp du beim Follow-up bist und wie du deine Wirkung maximierst.
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          className="w-full bg-white rounded-full h-3 mb-8 shadow-inner overflow-hidden"
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
          />
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200/50"
          >
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                  Frage {currentQuestion + 1} von {questions.length}
                </span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 leading-relaxed">
                {questions[currentQuestion].text}
              </h2>
            </div>

            <div className="space-y-4 mb-8">
              {questions[currentQuestion].options.map((option, index) => (
                <motion.label
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`block cursor-pointer p-6 rounded-2xl border-2 transition-all hover:shadow-lg ${
                    currentAnswer === option.value
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={questions[currentQuestion].id}
                    value={option.value}
                    onChange={() => handleAnswerSelect(option.value)}
                    className="sr-only"
                  />
                  <span className={`text-lg ${currentAnswer === option.value ? 'text-blue-800 font-semibold' : 'text-gray-700'}`}>
                    {option.text}
                  </span>
                </motion.label>
              ))}
            </div>

            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleNext}
                disabled={!currentAnswer}
                className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all flex items-center gap-2 ${
                  currentAnswer
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isLastQuestion ? 'Ergebnis anzeigen' : 'Weiter'}
                <SafeIcon icon={FiArrowRight} />
              </motion.button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default App;